import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  CheckCircle,
  Clock,
  Eye,
  Loader2,
  Users,
  BarChart2,
  AlertCircle,
} from 'lucide-react'

interface ParticipantSummary {
  id: string
  runParticipantId?: string | null
  userId?: string | null
  role?: string | null
  invitedAt?: string | null
  completedAt?: string | null
  isCompleted?: boolean
  user?: {
    id?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  } | null
}

interface CycleQuestion {
  id: string
  questionType: 'text' | 'rating' | 'multiple_choice'
  text: string
}

interface FeedbackSubmission {
  id: string
  participantId: string | null
  participantRole?: string | null
  respondent: {
    id?: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  } | null
  isAnonymous: boolean
  submittedAt: string | null
  answers: Array<{
    questionId: string
    questionText: string
    questionType: 'text' | 'rating' | 'multiple_choice'
    responseText?: string | null
    responseRating?: number | null
    responseChoice?: unknown
  }>
}

interface FeedbackRun {
  id: string
  runNumber: number
  triggeredAt: string | null
  dueAt: string | null
  triggerType?: string | null
  participants: ParticipantSummary[]
  submissions: FeedbackSubmission[]
  stats: {
    totalParticipants: number
    completedCount: number
    pendingCount: number
  }
}

interface FeedbackHistoryStats {
  totalInvitations: number
  totalRuns: number
  totalResponses: number
  lastSentAt: string | null
  nextRunDate: string | null
}

interface FeedbackHistoryData {
  cycle: {
    id: string
    name: string
    description?: string | null
    questions: CycleQuestion[]
    participants: ParticipantSummary[]
    lastSentAt?: string | null
    nextRunDate?: string | null
    schedule?: {
      dueInDays?: number
      nextRunDate?: string | null
      currentRunDate?: string | null
    } | null
  }
  stats: FeedbackHistoryStats
  runs: FeedbackRun[]
}

interface RecurringFeedbackHistoryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cycleName?: string
  loading: boolean
  error?: string | null
  history: FeedbackHistoryData | null
}

function formatDateValue(value?: string | null, withTime = false): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: withTime ? 'short' : undefined,
  })
}

function getParticipantName(participant: ParticipantSummary): string {
  const { user } = participant
  const first = user?.firstName ?? ''
  const last = user?.lastName ?? ''
  const full = [first, last].filter(Boolean).join(' ')
  if (full) return full
  if (user?.email) return user.email
  return 'Unassigned'
}

function formatChoice(value: unknown): string {
  if (value === null || value === undefined) {
    return '—'
  }
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(', ')
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return '[object]'
    }
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return '—'
  }
  return String(value)
}

export function RecurringFeedbackHistoryModal({
  open,
  onOpenChange,
  cycleName,
  history,
  loading,
  error,
}: RecurringFeedbackHistoryModalProps) {
  const summary = history?.stats
  const runs = history?.runs ?? []
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(runs[0]?.id ?? null)

  React.useEffect(() => {
    if (!runs.length) {
      setSelectedRunId(null)
      return
    }
    if (!selectedRunId || !runs.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(runs[0]?.id ?? null)
    }
  }, [runs, selectedRunId])

  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? runs[0] ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl sm:max-h-[90vh] flex flex-col">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Feedback History
          </DialogTitle>
          <DialogDescription>
            View invitations and responses for {cycleName ? `“${cycleName}”` : 'this recurring feedback cycle'}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading history…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-10 text-center">
              <AlertCircle className="h-10 w-10 text-destructive" />
              <p className="text-muted-foreground max-w-md">{error}</p>
            </div>
          ) : !history ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-10 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground max-w-md">No history available for this feedback cycle yet.</p>
            </div>
          ) : (
            <ScrollArea className="h-full pr-4">
              <div className="space-y-6 pb-4">
                {summary && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-border bg-muted/40 p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" /> Total Invitations Sent
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-foreground">{summary.totalInvitations}</div>
                      <p className="mt-1 text-xs text-muted-foreground">Across all recurring runs.</p>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/40 p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <BarChart2 className="h-4 w-4" /> Times Sent Out
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-foreground">{summary.totalRuns}</div>
                      <p className="mt-1 text-xs text-muted-foreground">Completed recurring runs.</p>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/40 p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4" /> Total Responses
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-foreground">{summary.totalResponses}</div>
                      <p className="mt-1 text-xs text-muted-foreground">Last sent {formatDateValue(summary.lastSentAt, true)}</p>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/40 p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" /> Next Run
                      </div>
                      <div className="mt-2 text-lg font-medium text-foreground">{formatDateValue(summary.nextRunDate, true)}</div>
                      <p className="mt-1 text-xs text-muted-foreground">Scheduled or estimated.</p>
                    </div>
                  </div>
                )}

                {runs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
                    No feedback runs have been sent yet.
                  </div>
                ) : (
                  <div className="grid gap-6 lg:grid-cols-[minmax(220px,260px)_1fr]">
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                      {runs.map((run, index) => {
                        const runLabel =
                          run.runNumber && run.runNumber > 0
                            ? `Run #${run.runNumber}`
                            : run.id === 'legacy'
                            ? 'Earlier responses'
                            : `Run ${runs.length - index}`

                        const completionLabel = `${run.stats.completedCount}/${run.stats.totalParticipants}`
                        const isActive = run.id === selectedRunId || (!selectedRunId && index === 0)

                        return (
                          <button
                            key={run.id}
                            type="button"
                            onClick={() => setSelectedRunId(run.id)}
                            className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                              isActive
                                ? 'border-primary/60 bg-primary/10 text-primary'
                                : 'border-border hover:border-primary/40 hover:bg-muted/40'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium">{runLabel}</p>
                                <p className="text-xs text-muted-foreground">
                                  Sent {formatDateValue(run.triggeredAt, true)}
                                  {run.dueAt ? ` • Due ${formatDateValue(run.dueAt, true)}` : ''}
                                </p>
                              </div>
                              <Badge variant={isActive ? 'default' : 'secondary'} className="text-xs">
                                {completionLabel}
                              </Badge>
                            </div>
                            <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-emerald-600" /> {run.stats.completedCount} completed
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {run.stats.pendingCount} pending
                              </span>
                            </div>
                          </button>
                        )
                      })}
                    </div>

                    {selectedRun ? (
                      <div className="space-y-6 min-h-[260px]">
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                            Participants
                          </h3>
                          {selectedRun.participants.length === 0 ? (
                            <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                              No reviewers were invited for this run.
                            </div>
                          ) : (
                            <div className="mt-3 grid gap-3 max-h-[320px] overflow-y-auto pr-1">
                              {selectedRun.participants.map((participant) => {
                                const name = getParticipantName(participant)
                                const statusLabel = participant.isCompleted ? 'Completed' : 'Pending'
                                const StatusIcon = participant.isCompleted ? CheckCircle : Clock

                                return (
                                  <div
                                    key={`${selectedRun.id}-${participant.id}`}
                                    className="rounded-xl border border-border bg-background/60 p-4 shadow-sm"
                                  >
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                      <div>
                                        <div className="text-base font-medium text-foreground">{name}</div>
                                        <div className="text-sm text-muted-foreground">
                                          {participant.user?.email ?? 'No email on record'}
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground space-y-1">
                                          <div>Invited: {formatDateValue(participant.invitedAt, true)}</div>
                                          <div>Completed: {formatDateValue(participant.completedAt, true)}</div>
                                        </div>
                                      </div>
                                      <Badge
                                        variant={participant.isCompleted ? 'default' : 'secondary'}
                                        className="flex items-center gap-1"
                                      >
                                        <StatusIcon className="h-3 w-3" />
                                        {statusLabel}
                                      </Badge>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>

                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                            Responses ({selectedRun.submissions.length})
                          </h3>
                          {selectedRun.submissions.length === 0 ? (
                            <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                              No feedback responses submitted yet.
                            </div>
                          ) : (
                            <Accordion type="multiple" className="mt-3 max-h-[360px] overflow-y-auto pr-1">
                              {selectedRun.submissions.map((submission) => {
                                const respondentName = submission.isAnonymous
                                  ? 'Anonymous respondent'
                                  : submission.respondent
                                  ? [submission.respondent.firstName, submission.respondent.lastName]
                                      .filter(Boolean)
                                      .join(' ') || submission.respondent.email || 'Unnamed respondent'
                                  : 'Unknown respondent'

                                return (
                                  <AccordionItem
                                    key={`${selectedRun.id}-${submission.id}`}
                                    value={`${selectedRun.id}-${submission.id}`}
                                    className="border border-border rounded-lg mb-3 bg-background/60"
                                  >
                                    <AccordionTrigger className="px-4 py-3 text-left">
                                      <div className="flex flex-col gap-1 text-sm">
                                        <span className="font-medium text-foreground">{respondentName}</span>
                                        <span className="text-xs text-muted-foreground">
                                          Submitted {formatDateValue(submission.submittedAt, true)}
                                        </span>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                      <div className="px-4 pb-4 space-y-4">
                                        {submission.answers.map((answer) => (
                                          <div key={`${submission.id}-${answer.questionId}`} className="space-y-2">
                                            <div className="text-sm font-medium text-foreground">
                                              {answer.questionText}
                                            </div>
                                            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-foreground">
                                              {answer.questionType === 'text' && (
                                                <p className="whitespace-pre-wrap">{answer.responseText || '—'}</p>
                                              )}
                                              {answer.questionType === 'rating' && (
                                                <p>Rating: {answer.responseRating ?? '—'}</p>
                                              )}
                                              {answer.questionType === 'multiple_choice' && (
                                                <p>Selected: {formatChoice(answer.responseChoice)}</p>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                )
                              })}
                            </Accordion>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
                        Select a run to view its details.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
